import React from "react";
import { useParams } from "react-router-dom";

const _cacheTasks = JSON.parse(localStorage.getItem("CHECKLIST_TASKS")) || [];
const _cacheRoles = JSON.parse(localStorage.getItem("CHECKLIST_ROLES")) || [];
const _cacheClosedNewsFlashes = JSON.parse(localStorage.getItem("CHECKLIST_CLOSED_NEWSFLASHES")) || [];

const Global = React.createContext();
Global.displayName = "Global";

const GlobalProvider = (props) => {
  const params = useParams();
  const { taskId, data } = props;
  const [showAllTasks, setShowAllTasks] = React.useState(data?.roles?.role ? false : true);
  const [activePhase, setActivePhase] = React.useState({});
  const [activeTask, setActiveTask] = React.useState({});
  const [tasksOpenOtherRoles, setTasksOpenOtherRoles] = React.useState([]);

  const [phases, setPhases] = React.useState(data.phases?.phase.map((p, i) => ({ ...p, id: i })));

  const [roles, setRoles] = React.useState(
      (data?.roles?.role || []).map((r) => {
      const cr = _cacheRoles.find((cr) => cr.$?.id === r.$?.id);
      return {
        ...r,
        checked: cr ? cr.checked : r.$["active"] === "true" ? true : false,
      };
    })
  );

  const [tasks, setTasks] = React.useState([]);

  const [closedNewsFlashes, setClosedNewsFlashes] = React.useState(_cacheClosedNewsFlashes);

  const openNewsFlash = () => {
    const newClosedNewsFlashes = Array.from(new Set(closedNewsFlashes).delete(data.newsflash?.$?.id));
    setClosedNewsFlashes(newClosedNewsFlashes);
    localStorage.setItem("CHECKLIST_CLOSED_NEWSFLASHES", JSON.stringify(newClosedNewsFlashes));
    return newClosedNewsFlashes;
  };

  const closeNewsFlash = () => {
    const newClosedNewsFlashes = Array.from(new Set(closedNewsFlashes).add(data.newsflash?.$?.id));
    setClosedNewsFlashes(newClosedNewsFlashes);
    localStorage.setItem("CHECKLIST_CLOSED_NEWSFLASHES", JSON.stringify(newClosedNewsFlashes));
    return newClosedNewsFlashes;
  };

  React.useEffect(() => {
    let _tasks = [];

    phases.map((p, i) => p.tasks.task.forEach((t) => _tasks.push({ ...t, phase_id: i })));

    _tasks = _tasks.map((t) => {
      const ct = _cacheTasks.find((ct) => ct.$.id == t.$.id);
      return {
        ...t,
        checked: ct ? ct.checked : t.checked,
        $: {
          ...t.$,
          id: t.$.id || String(Math.floor(Math.random() * 1000)),
        },
        isActiveForCurrentRoles: isActiveForCurrentRoles(t),
      };
    });

    setTasks(_tasks);
  }, [phases, roles]);

  const toggleTask = React.useCallback((phase_id, id, value = null) => {
    setTasks((prevState) => {
      const newState = prevState.map((t) => {
        if (t.$.id === id && t.phase_id === phase_id) {
          return {
            ...t,
            checked: value === null ? !t.checked : value,
          };
        } else {
          return t;
        }
      });
      localStorage.setItem("CHECKLIST_TASKS", JSON.stringify(newState));
      return newState;
    });
  }, []);

  const toggleRole = React.useCallback((id, value = null) => {
    setRoles((prevState) => {
      const newState = prevState.map((r) => {
        if (r.$.id === id) {
          return {
            ...r,
            checked: value === null ? !r.checked : value,
          };
        } else {
          return r;
        }
      });
      localStorage.setItem("CHECKLIST_ROLES", JSON.stringify(newState));
      return newState;
    });
  }, []);

  const isActiveForCurrentRoles = React.useCallback(
    (task) => {
      return roles.filter((r) => r.checked && task.$.role?.split(" ")?.includes(r.$.id))?.length ? true : false;
    },
    [roles]
  );

  const rolesForTask = React.useCallback((task) => {
    return task.$.role?.split(" ")?.filter((t) => t) || [];
  });

  const tasksForRoleID = React.useCallback(
    (id) => {
      return tasks.filter((t) => rolesForTask(t).length && t).filter((t) => (id ? rolesForTask(t).includes(id) : t));
    },
    [tasks]
  );

  const tasksForPhaseID = React.useCallback(
    (id) => {
      return tasks.filter((t) => t && Number(t.phase_id) === Number(id));
    },
    [tasks, tasksOpenOtherRoles, roles]
  );

  const tasksBundled = React.useCallback(
    (id) => {
      let bundles = [];
      const _tasks = tasksForPhaseID(id);

      _tasks.forEach((t, i) => {
        if (t.isActiveForCurrentRoles || tasksOpenOtherRoles?.includes(t.$.id) || showAllTasks) {
          //if prev active bundle exists
          if (_tasks[i - 1] && _tasks[i - 1].isActiveForCurrentRoles && bundles[i - 1]?.type === "active") {
            bundles[i - 1].tasks.push(t);
            //if prev active bundle does not exist
          } else {
            bundles.push({ type: "active", tasks: [t] });
          }
        } else {
          if (rolesForTask(t).length) {
            //if prev passive bundle exists
            let lastBundle = bundles[bundles.length - 1];

            if (_tasks[i - 1] && !_tasks[i - 1].isActiveForCurrentRoles && lastBundle?.type === "passive") {
              lastBundle.tasks.push(t);
              //if prev passive bundle does not exist
            } else {
              bundles.push({ type: "passive", tasks: [t] });
            }
          } else {
            // //if prev noRole bundle exists
            if (_tasks[i - 1] && rolesForTask(_tasks[i - 1]).length === 0 && bundles[i - 1]?.type === "noRole") {
              bundles[i - 1].tasks.push(t);
              //if prev noRole bundle does not exist
            } else {
              bundles.push({ type: "noRole", tasks: [t] });
            }
          }
        }
      });
      return bundles;
    },
    [tasks, tasksOpenOtherRoles, roles, showAllTasks]
  );

  const tasksTotal = React.useCallback(
    (phase_id = null) => {
      const _tasks = phase_id === null ? ((roles.length > 0) ? tasks.filter((t) => rolesForTask(t).length) : tasks) : ((roles.length > 0) ? tasksForPhaseID(phase_id).filter((t) => rolesForTask(t).length) : tasksForPhaseID(phase_id));

      return showAllTasks ? _tasks.length : _tasks?.filter((t) => t.isActiveForCurrentRoles).length;
    },
    [tasks, showAllTasks, roles]
  );

  const tasksChecked = React.useCallback(
    (phase_id = null) => {
      const _tasks = phase_id === null ? tasks : tasksForPhaseID(phase_id);
      return _tasks?.filter((t) => (t.isActiveForCurrentRoles || showAllTasks) && t.checked).length;
    },
    [tasks, showAllTasks, roles]
  );

  const firstUncheckedTaskForPhaseID = React.useCallback((id) => {
    const _tasks = tasksForPhaseID(id).filter((t) => t.isActiveForCurrentRoles);
    let firstUnchecked = null;
    _tasks.forEach((t) => {
      if (!firstUnchecked && !t.checked) firstUnchecked = t;
    });
    return firstUnchecked || _tasks[0];
  });

  const value = React.useMemo(() => ({
    data,
    phases,
    activePhase,
    tasksForPhaseID,
    setActivePhase,
    roles,
    toggleRole,
    isActiveForCurrentRoles,
    tasksForRoleID,
    tasks,
    tasksBundled,
    setTasks,
    activeTask,
    setActiveTask,
    toggleTask,
    tasksOpenOtherRoles,
    setTasksOpenOtherRoles,
    showAllTasks,
    setShowAllTasks,
    firstUncheckedTaskForPhaseID,
    tasksTotal,
    tasksChecked,
    closedNewsFlashes,
    closeNewsFlash,
    openNewsFlash,
  }));

  return <Global.Provider value={value} {...props} />;
};

function useGlobalState() {
  const context = React.useContext(Global);
  if (context === undefined) {
    throw new Error("useGlobal must be used within a GlobalProvider");
  }
  return context;
}

export { GlobalProvider, useGlobalState };
